import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/components/NotificationBell';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, AlertCircle, DollarSign, CalendarDays, CheckCircle2, Clock, CreditCard, Loader2, ArrowRight } from 'lucide-react';

const APPOINTMENT_TYPES = ['barber', 'beauty', 'dental', 'gym'];

export const UserDashboard = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const isAppointmentType = APPOINTMENT_TYPES.includes(user?.business_type);

  const [paymentRecords, setPaymentRecords] = useState([]);
  const [payments, setPayments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    if (isAppointmentType) {
      fetchAppointments();
    } else {
      fetchPayments();
    }
  }, []);

  const fetchPayments = async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/payment-records`, { withCredentials: true }),
        axios.get(`${API_URL}/api/payments`, { withCredentials: true }),
      ]);
      setPaymentRecords(rRes.data);
      setPayments(pRes.data);
    } catch (e) { /* empty */ }
  };

  const fetchAppointments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/appointments`, { withCredentials: true });
      setAppointments(data);
    } catch (e) { /* empty */ }
  };

  const handleQuickPay = async (recordId) => {
    setPayingId(recordId);
    try {
      const { data } = await axios.post(`${API_URL}/api/checkout/create`, {
        record_id: recordId,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ödeme başlatılamadı. İşletmenize başvurun.');
      setPayingId(null);
    }
  };

  const getPaymentTitle = (pid) => payments.find((p) => p.id === pid)?.title || 'Ödeme';

  const pendingRecords = paymentRecords.filter((r) => r.status === 'pending');
  const overdueRecords = paymentRecords.filter((r) => r.status === 'overdue');
  const paidRecords = paymentRecords.filter((r) => r.status === 'paid');
  const totalPending = pendingRecords.reduce((sum, r) => sum + r.amount, 0);
  const totalOverdue = overdueRecords.reduce((sum, r) => sum + r.amount, 0);

  // Highest priority record: overdue first, then oldest pending
  const priorityRecord = (() => {
    if (overdueRecords.length > 0) return overdueRecords[0];
    if (pendingRecords.length > 0) {
      return [...pendingRecords].sort((a, b) => {
        const da = payments.find(p => p.id === a.payment_id)?.due_date || '';
        const db_ = payments.find(p => p.id === b.payment_id)?.due_date || '';
        return new Date(da) - new Date(db_);
      })[0];
    }
    return null;
  })();

  const scheduledApts = appointments.filter((a) => a.status === 'scheduled');
  const completedApts = appointments.filter((a) => a.status === 'completed');
  const cancelledApts = appointments.filter((a) => a.status === 'cancelled');

  return (
    <PageLayout>
      
      
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="user-dashboard-title">
                Ana Sayfa
              </h1>
              <p className="text-base text-[#595959] mt-2">
                {isAppointmentType ? 'Randevu özetiniz' : 'Ödeme özetiniz'}
              </p>
            </div>
            <NotificationBell />
          </div>

          {isAppointmentType ? (
            /* Appointment Dashboard */
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="border border-[#E5E5E5]" data-testid="card-scheduled">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base font-medium text-[#595959]">Bekleyen Randevu</CardTitle>
                    <Clock className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#1A1A1A]">{scheduledApts.length}</div>
                    <p className="text-sm text-[#595959] mt-1">planlanmış randevu</p>
                  </CardContent>
                </Card>

                <Card className="border border-[#E5E5E5]" data-testid="card-completed">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base font-medium text-[#595959]">Tamamlanan</CardTitle>
                    <CheckCircle2 className="w-5 h-5 text-[#16A34A]" strokeWidth={1.5} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#16A34A]">{completedApts.length}</div>
                    <p className="text-sm text-[#595959] mt-1">tamamlanmış randevu</p>
                  </CardContent>
                </Card>

                <Card className="border border-[#E5E5E5]" data-testid="card-cancelled">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base font-medium text-[#595959]">İptal Edilen</CardTitle>
                    <CalendarDays className="w-5 h-5 text-[#DC2626]" strokeWidth={1.5} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#DC2626]">{cancelledApts.length}</div>
                    <p className="text-sm text-[#595959] mt-1">iptal edilen randevu</p>
                  </CardContent>
                </Card>
              </div>

              {scheduledApts.length > 0 && (
                <Card className="border border-[#E5E5E5]" data-testid="upcoming-appointments">
                  <CardHeader>
                    <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                      <CalendarDays className="w-5 h-5" />
                      Yaklaşan Randevular
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {scheduledApts.slice(0, 5).map((apt) => (
                        <div key={apt.id} className="flex items-center justify-between p-3 bg-[#F9F8F6] rounded-lg" data-testid={`upcoming-apt-${apt.id}`}>
                          <div>
                            <p className="font-medium text-[#1A1A1A]">{apt.service}</p>
                            <p className="text-sm text-[#595959]">{apt.date} | {apt.start_time} - {apt.end_time}</p>
                          </div>
                          {apt.price && <span className="font-medium text-[#2563EB]">{apt.price} TL</span>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            /* Payment Dashboard */
            <>
              {/* Quick Pay Hero Card */}
              {priorityRecord ? (
                <Card
                  className={`mb-6 border-2 ${priorityRecord.status === 'overdue' ? 'border-[#DC2626] bg-gradient-to-br from-[#FFF5F2] to-white' : 'border-[#2563EB] bg-gradient-to-br from-[#F0F5F2] to-white'}`}
                  data-testid="quick-pay-card"
                >
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {priorityRecord.status === 'overdue' ? (
                        <>
                          <AlertCircle className="w-5 h-5 text-[#DC2626]" />
                          <span className="text-sm font-semibold text-[#DC2626]" data-testid="quick-pay-label">Vadesi Geçmiş Ödeme</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-5 h-5 text-[#2563EB]" />
                          <span className="text-sm font-semibold text-[#2563EB]" data-testid="quick-pay-label">Bekleyen Ödemeniz</span>
                        </>
                      )}
                    </div>

                    <p className="text-sm text-[#595959] mb-1" data-testid="quick-pay-title">{getPaymentTitle(priorityRecord.payment_id)}</p>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-4xl sm:text-5xl font-bold text-[#1A1A2E] tabular-nums" data-testid="quick-pay-amount">
                        {priorityRecord.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-xl font-semibold text-[#595959]">TL</span>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={() => handleQuickPay(priorityRecord.id)}
                        disabled={payingId === priorityRecord.id}
                        className={`flex-1 h-12 text-base font-semibold gap-2 ${priorityRecord.status === 'overdue' ? 'bg-[#DC2626] hover:bg-[#a34936]' : 'bg-[#2563EB] hover:bg-[#1E4ED8]'} text-white`}
                        data-testid="quick-pay-btn"
                      >
                        {payingId === priorityRecord.id ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> Yönlendiriliyor...</>
                        ) : (
                          <><CreditCard className="w-5 h-5" /> Hemen Öde</>
                        )}
                      </Button>
                      {(pendingRecords.length + overdueRecords.length) > 1 && (
                        <Button
                          variant="outline"
                          className="sm:w-auto h-12 gap-1"
                          onClick={() => window.location.href = '/payments'}
                          data-testid="view-all-payments-btn"
                        >
                          Tümünü Gör ({pendingRecords.length + overdueRecords.length})
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : paymentRecords.length > 0 && (
                <Card className="mb-6 border-2 border-[#16A34A] bg-gradient-to-br from-[#F0F9F4] to-white" data-testid="all-paid-card">
                  <CardContent className="p-5 flex items-center gap-3">
                    <div className="p-2.5 rounded-full bg-[#16A34A]/10">
                      <CheckCircle2 className="w-6 h-6 text-[#16A34A]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#16A34A]">Tüm ödemeleriniz tamamlandı</p>
                      <p className="text-sm text-[#595959]">Bekleyen ödemeniz bulunmuyor.</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <Card className="border border-[#E5E5E5]" data-testid="card-pending-payments">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base font-medium text-[#595959]">Bekleyen</CardTitle>
                    <Receipt className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#1A1A1A]">{totalPending.toFixed(2)} TL</div>
                    <p className="text-sm text-[#595959] mt-1">{pendingRecords.length} ödeme</p>
                  </CardContent>
                </Card>

                <Card className="border border-[#E5E5E5]" data-testid="card-overdue-payments">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base font-medium text-[#595959]">Gecikmiş</CardTitle>
                    <AlertCircle className="w-5 h-5 text-[#DC2626]" strokeWidth={1.5} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#DC2626]">{totalOverdue.toFixed(2)} TL</div>
                    <p className="text-sm text-[#595959] mt-1">{overdueRecords.length} ödeme</p>
                  </CardContent>
                </Card>

                <Card className="border border-[#E5E5E5]" data-testid="card-paid-payments">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base font-medium text-[#595959]">Ödenen</CardTitle>
                    <DollarSign className="w-5 h-5 text-[#16A34A]" strokeWidth={1.5} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-[#16A34A]">{paidRecords.length}</div>
                    <p className="text-sm text-[#595959] mt-1">ödeme tamamlandı</p>
                  </CardContent>
                </Card>
              </div>

              {overdueRecords.length > 0 && (
                <Card className="border border-[#DC2626] mb-8" data-testid="overdue-alert">
                  <CardHeader>
                    <CardTitle className="text-xl text-[#DC2626] flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Gecikmiş Ödemeler
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#595959]">
                      {overdueRecords.length} adet gecikmiş ödemeniz bulunmaktadır.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      
    </PageLayout>
  );
};
